import {
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  Search,
} from 'lucide-react'
import { useState } from 'react'
import axios from 'axios'

/*
  This example requires some changes to your config:

  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/

// Define types for the form and API
type ApiBudget =
  | 'less_than_50M'
  | '50M_to_250M'
  | '250M_to_500M'
  | 'more_than_500M'

interface FormData {
  firstName: string
  lastName: string
  email: string
  company: string
  phone: string
  message: string
  budget: ApiBudget
}

interface FormStatus {
  isSubmitting: boolean
  isSuccess: boolean
  isError: boolean
  message: string
}

export default function GetInTouch() {
  const [formStatus, setFormStatus] = useState<FormStatus>({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    message: '',
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setFormStatus({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
      message: '',
    })

    const form = event.target as HTMLFormElement
    const formData = new FormData(form)

    // Extract form values and map to API format
    const payload: FormData = {
      firstName: formData.get('first-name') as string,
      lastName: formData.get('last-name') as string,
      email: formData.get('email') as string,
      company: formData.get('company') as string,
      phone: formData.get('phone') as string,
      message: formData.get('message') as string,
      budget: formData.get('budget') as ApiBudget,
    }

    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        'https://api.ipdcgroup.com/contact',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )

      setFormStatus({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
        message:
          response.data.message || 'Your message has been sent successfully!',
      })

      // Reset form after successful submission
      form.reset()
    } catch (error) {
      console.error('Error submitting the form:', error)
      let errorMessage =
        'There was an error sending your message. Please try again later.'

      // Extract error message from Axios error if available
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        errorMessage = error.response.data.message
      }

      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        message: errorMessage,
      })
    }
  }

  return (
    <div className="relative bg-white">
      <div className="lg:absolute lg:inset-0 lg:left-1/2">
        <img
          alt=""
          src="/get_in_touch_cover.png"
          className="h-64 w-full bg-gray-50 object-cover sm:h-80 lg:absolute lg:h-full"
        />
      </div>
      <div className="pb-24 pt-8 sm:pb-8 sm:pt-12 lg:mx-auto lg:grid lg:max-w-7xl lg:grid-cols-2 lg:pt-8">
        <div className="px-6 lg:px-8">
          <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Let's work together
            </h2>
            <p className="mt-2 text-lg leading-8 text-gray-500">
              Want to get started on your next construction project? Maybe a new
              estate? Let's get started
            </p>

            <div className="my-2">
              <div className="space-y-4">
                <p className="flex items-center">
                  <Phone className="mr-4 text-red-600" /> +234 803 600 6188
                </p>
                <p className="flex items-center">
                  <Mail className="mr-4 text-red-600" /> info@ipdc.com.ng
                </p>
                <p className="flex items-center">
                  <MapPin className="mr-4 text-red-600" /> Plot 620, Katampe Rd,
                  Jahi District, Abuja
                </p>
              </div>
            </div>

            {formStatus.isSuccess && (
              <div className="mt-6 rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-green-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      {formStatus.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {formStatus.isError && (
              <div className="mt-6 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      {formStatus.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} method="POST" className="mt-16">
              <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="first-name"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    First name
                  </label>
                  <div className="mt-2.5">
                    <input
                      required
                      id="first-name"
                      name="first-name"
                      type="text"
                      autoComplete="given-name"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="last-name"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Last name
                  </label>
                  <div className="mt-2.5">
                    <input
                      required
                      id="last-name"
                      name="last-name"
                      type="text"
                      autoComplete="family-name"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Email
                  </label>
                  <div className="mt-2.5">
                    <input
                      required
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="company"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Company
                  </label>
                  <div className="mt-2.5">
                    <input
                      id="company"
                      name="company"
                      type="text"
                      autoComplete="organization"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex justify-between text-sm leading-6">
                    <label
                      htmlFor="phone"
                      className="block font-semibold text-gray-900"
                    >
                      Phone
                    </label>
                    <p id="phone-description" className="text-gray-400">
                      Optional
                    </p>
                  </div>
                  <div className="mt-2.5">
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      aria-describedby="phone-description"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex justify-between text-sm leading-6">
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold leading-6 text-gray-900"
                    >
                      How can we help you?
                    </label>
                    <p id="message-description" className="text-gray-400">
                      Max 500 characters
                    </p>
                  </div>
                  <div className="mt-2.5">
                    <textarea
                      required
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      aria-describedby="message-description"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-red-600 sm:text-sm sm:leading-6"
                      defaultValue={''}
                    />
                  </div>
                </div>
                <fieldset className="sm:col-span-2">
                  <legend className="block text-sm font-semibold leading-6 text-gray-900">
                    Expected budget
                  </legend>
                  <div className="mt-4 space-y-4 text-sm leading-6 text-gray-600">
                    <div className="flex gap-x-2.5">
                      <input
                        defaultChecked
                        value="less_than_50M"
                        id="budget-under-25k"
                        name="budget"
                        type="radio"
                        className="mt-1 h-4 w-4 border-gray-300 text-red-600 shadow-sm focus:ring-red-600"
                      />
                      <label htmlFor="budget-under-25k">Less than ₦50M</label>
                    </div>
                    <div className="flex gap-x-2.5">
                      <input
                        value="50M_to_250M"
                        id="budget-25k-50k"
                        name="budget"
                        type="radio"
                        className="mt-1 h-4 w-4 border-gray-300 text-red-600 shadow-sm focus:ring-red-600"
                      />
                      <label htmlFor="budget-25k-50k">₦50M – ₦250M</label>
                    </div>
                    <div className="flex gap-x-2.5">
                      <input
                        value="250M_to_500M"
                        id="budget-50k-100k"
                        name="budget"
                        type="radio"
                        className="mt-1 h-4 w-4 border-gray-300 text-red-600 shadow-sm focus:ring-red-600"
                      />
                      <label htmlFor="budget-50k-100k">₦250M – ₦500M</label>
                    </div>
                    <div className="flex gap-x-2.5">
                      <input
                        value="more_than_500M"
                        id="budget-over-100k"
                        name="budget"
                        type="radio"
                        className="mt-1 h-4 w-4 border-gray-300 text-red-600 shadow-sm focus:ring-red-600"
                      />
                      <label htmlFor="budget-over-100k">₦500M+</label>
                    </div>
                  </div>
                </fieldset>
              </div>
              <div className="mt-10 flex justify-end border-t border-gray-900/10 pt-8">
                <button
                  type="submit"
                  disabled={formStatus.isSubmitting}
                  className="rounded-md bg-red-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-70"
                >
                  {formStatus.isSubmitting ? 'Sending...' : 'Send message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
